import {CommonModule} from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    input,
    OnInit,
    Self,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormArray,
    FormBuilder,
    FormsModule,
    NgControl,
    ReactiveFormsModule,
} from '@angular/forms';
import {NgbModule, NgbPopover} from '@ng-bootstrap/ng-bootstrap';
import {TranslatePipe} from '@brunata-angular/util';
import {v4 as uuidv4} from 'uuid';
import {merge, map, Subject} from 'rxjs';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {PopoverIconComponent} from '../popover-icon/popover-icon.component';
import {
    CheckBoxControl,
    createCheckBoxControl,
    reapplyItemDisabledState,
    SelectableItem,
} from '../input-checkboxes/input-checkboxes.component';

@Component({
    selector: 'app-input-dropdown-checkboxes',
    templateUrl: './input-dropdown-checkboxes.component.html',
    styleUrl: './input-dropdown-checkboxes.component.scss',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        NgbModule,
        NgbPopover,
        TranslatePipe,
        PopoverIconComponent,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputDropdownCheckboxesComponent implements OnInit, ControlValueAccessor {
    @Input() label = '';
    @Input() required = false;
    @Input() id: string = uuidv4();
    @Input() information: string | undefined;
    /** Lets a wrapping control value accessor show its own control's invalid state on the select. */
    readonly invalid = input(false);
    readonly placeholder = input('');

    formGroup = this.formBuilder.group({formArray: new FormArray<CheckBoxControl>([])});
    private readonly valueWritten = new Subject<void>();

    protected readonly anySelected = toSignal(
        merge(this.formGroup.controls.formArray.valueChanges, this.valueWritten).pipe(
            map(() => this.formGroup.controls.formArray.value.some(item => item.selected === true)),
        ),
        {initialValue: false},
    );

    private onTouch: (() => void) | undefined;
    private onChange: ((value: SelectableItem[]) => void) | undefined;

    constructor(
        @Self() public ngControl: NgControl,
        private changeDetectorRef: ChangeDetectorRef,
        private formBuilder: FormBuilder,
    ) {
        ngControl.valueAccessor = this;
        this.formGroup.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
            this.onTouch?.();
            this.onChange?.(this.formGroup.controls.formArray.getRawValue());
        });
        this.formGroup.controls.formArray.statusChanges
            .pipe(takeUntilDestroyed())
            .subscribe(status => {
                if (status === 'PENDING') {
                    this.ngControl.control?.markAsPending();
                } else {
                    this.ngControl.control?.setErrors(this.formGroup.controls.formArray.errors);
                }
            });
    }

    get labelClass(): string {
        return `${this.required ? 'required' : ''}`;
    }

    deselectAll(): void {
        this.formGroup.controls.formArray.controls.forEach(item => this.setValue(item, false));
    }

    ngOnInit(): void {
        /*  Then using ChangeDetectionStrategy.OnPush the component doesn't notice, when the control is marked as
         touched, and will therefore not show validation errors. However, Angular doesn't offer a simple way of
         listening to, when a control is marked as touched, that we can hook into. So we have to reassign the
         markAsTouched() method in order to call detectChanges(). */
        if (this.ngControl.control != null) {
            const originalMarkAsTouched = this.ngControl.control.markAsTouched.bind(
                this.ngControl.control,
            );
            const changeDetectorRef = this.changeDetectorRef;
            this.ngControl.control.markAsTouched = function (...args): void {
                originalMarkAsTouched.apply(this, args);
                changeDetectorRef.detectChanges();
            };
        }
    }

    setValue(checkBoxControl: CheckBoxControl, value: boolean): void {
        if (checkBoxControl.controls.selected.disabled) {
            return;
        }
        checkBoxControl.controls.selected.setValue(value);
    }

    registerOnChange(fn: (value: SelectableItem[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    writeValue(values: SelectableItem[]): void {
        this.formGroup.controls.formArray.clear({emitEvent: false});
        values.forEach(item => {
            this.formGroup.controls.formArray.push(createCheckBoxControl(item), {emitEvent: false});
        });
        if (this.ngControl.disabled === true) {
            this.formGroup.disable({emitEvent: false});
        }
        this.valueWritten.next();
        this.changeDetectorRef.markForCheck();
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.formGroup.disable();
        } else {
            this.formGroup.enable();
            reapplyItemDisabledState(this.formGroup.controls.formArray.controls);
        }
        this.changeDetectorRef.markForCheck();
    }
}
